/**
 * useSoloStatus Hook
 *
 * Fetches solo provider status for SoloLayout.
 * Used to validate subscription and setup state.
 */

import { useQuery } from '@tanstack/react-query';
import { bootstrap } from '@/shared/api/bootstrap';
import type { SoloStatusResponse } from '@/shared/api/bootstrap';
import { logger } from '@/shared/utils/logger';
import { bootstrapKeys } from './useBootstrap';

export interface UseSoloStatusOptions {
  enabled?: boolean;
  accessToken?: string | null;
}

export interface UseSoloStatusReturn {
  data: SoloStatusResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

/**
 * Hook to fetch solo provider status for SoloLayout
 */
export const useSoloStatus = (options: UseSoloStatusOptions = {}): UseSoloStatusReturn => {
  const { enabled = true, accessToken } = options;

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<SoloStatusResponse>({
    queryKey: bootstrapKeys.soloStatus(),
    queryFn: async () => {
      logger.info('Fetching solo provider status');
      const result = await bootstrap.getSoloStatus(accessToken);
      logger.info('Solo status received', {
        hasProviderProfile: result.providerProfile.exists,
        setupComplete: result.providerProfile.setupComplete,
        subscriptionStatus: result.providerProfile.soloSubscriptionStatus,
      });
      return result;
    },
    enabled,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    data: data || null,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};
